import { useState, useCallback, useEffect } from "react";
import { X } from "lucide-react";
import Reveal from "./Reveal";
import { BTS } from "../data/bts";

/**
 * Masonry-style behind-the-scenes gallery.
 * CSS columns give a true Pinterest-style layout where each image keeps its
 * natural aspect ratio. Clicking an image opens a full-screen lightbox.
 */
export default function BtsGallery() {
  const [openIdx, setOpenIdx] = useState(-1);

  const close = useCallback(() => setOpenIdx(-1), []);
  const next = useCallback(
    () => setOpenIdx((i) => (i + 1) % BTS.length),
    []
  );
  const prev = useCallback(
    () => setOpenIdx((i) => (i - 1 + BTS.length) % BTS.length),
    []
  );

  useEffect(() => {
    if (openIdx < 0) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [openIdx, close, next, prev]);

  const active = openIdx >= 0 ? BTS[openIdx] : null;

  return (
    <div data-testid="bts-gallery">
      <div className="[column-count:1] sm:[column-count:2] lg:[column-count:3] [column-gap:1rem]">
        {BTS.map((img, i) => (
          <Reveal key={img.src} delay={(i % 6) * 40}>
            <button
              type="button"
              onClick={() => setOpenIdx(i)}
              className="mb-4 block w-full overflow-hidden rounded-md border border-white/10 bg-white/[0.02] group [break-inside:avoid] focus:outline-none focus:ring-2 focus:ring-[var(--yellow)]"
              data-testid={`bts-item-${i}`}
              aria-label={`Open ${img.alt}`}
            >
              <img
                src={img.src}
                alt={img.alt}
                width={img.w}
                height={img.h}
                loading="lazy"
                className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.03]"
              />
            </button>
          </Reveal>
        ))}
      </div>

      {active && (
        <div
          className="fixed inset-0 z-[80] bg-black/95 flex items-center justify-center p-4"
          onClick={close}
          data-testid="bts-lightbox"
        >
          <button
            type="button"
            onClick={close}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
            aria-label="Close"
            data-testid="bts-lightbox-close"
          >
            <X size={20} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white z-10"
            aria-label="Previous"
            data-testid="bts-lightbox-prev"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white z-10"
            aria-label="Next"
            data-testid="bts-lightbox-next"
          >
            ›
          </button>
          <img
            src={active.src}
            alt={active.alt}
            className="max-h-[90vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
